import toast from 'react-hot-toast';
import type { LucideIcon } from 'lucide-react';
import { BACKUP_TABLE_LABELS } from '@genealogy-app/shared';

// ─── Bausteine der Sicherungs-Ansichten ──────────────────────────────────────
//
// Gemeinsam genutzt von der Sicherung eines einzelnen Baums und von der
// Wiederherstellung eines Baums aus dem Gesamt-Backup. Beide zeigen dieselbe
// Gegenüberstellung „jetzt / danach" und dieselben Karten.

export const LABELS = BACKUP_TABLE_LABELS;

/** Rohdaten (z. B. ZIP) als Datei herunterladen. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** JSON als Datei herunterladen. */
export function downloadJson(data: unknown, filename: string) {
  downloadBlob(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }), filename);
}

/** Zeitstempel für Dateinamen: 2026-07-30-14-05-11 */
export function fileStamp(): string {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
}

export function Card({
  title,
  description,
  icon: Icon,
  tone = 'neutral',
  children,
}: {
  title: string;
  description: string;
  icon: LucideIcon;
  tone?: 'neutral' | 'danger';
  children: React.ReactNode;
}) {
  return (
    <section
      className={`rounded-xl border bg-white p-4 sm:p-5 ${
        tone === 'danger' ? 'border-red-200' : 'border-slate-200'
      }`}
    >
      <div className="flex items-start gap-3">
        <Icon
          className={`mt-0.5 h-5 w-5 shrink-0 ${tone === 'danger' ? 'text-red-500' : 'text-slate-400'}`}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{description}</p>
          <div className="mt-3">{children}</div>
        </div>
      </div>
    </section>
  );
}

/**
 * Gegenüberstellung je Tabelle. Zeigt nur Zeilen, in denen etwas steht – eine
 * Liste aus fünfzehn Nullen sagt nichts.
 */
export function DiffTable({
  current,
  incoming,
}: {
  current: Record<string, number>;
  incoming: Record<string, number>;
}) {
  const keys = Object.keys(LABELS).filter(
    (k) => (current[k] ?? 0) > 0 || (incoming[k] ?? 0) > 0,
  );
  if (keys.length === 0) {
    return <p className="text-xs text-slate-500">Die Sicherung enthält keine Daten.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[20rem] text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-400">
            <th className="py-1.5 pr-3 font-medium">Inhalt</th>
            <th className="py-1.5 pr-3 text-right font-medium">jetzt</th>
            <th className="py-1.5 text-right font-medium">danach</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => {
            const now = current[k] ?? 0;
            const next = incoming[k] ?? 0;
            // Rot, wo Datensätze wegfallen – das ist die Zahl, die man vor dem
            // Bestätigen wirklich lesen muss.
            const loses = next < now;
            return (
              <tr key={k} className="border-b border-slate-100 last:border-0">
                <td className="py-1.5 pr-3 text-slate-700">{LABELS[k]}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums text-slate-500">{now}</td>
                <td
                  className={`py-1.5 text-right font-medium tabular-nums ${
                    loses ? 'text-red-600' : 'text-slate-700'
                  }`}
                >
                  {next}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Warnhinweis in Bernstein – vor dem Bestätigen zu lesen. */
export function WarningLine({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">
      <span aria-hidden="true">⚠</span>
      <span>{children}</span>
    </p>
  );
}

/**
 * Hinweise nach einer Wiederherstellung einzeln und länger anzeigen – im
 * Erfolgs-Toast würden sie untergehen.
 */
export function showNotes(notes: unknown) {
  if (!Array.isArray(notes)) return;
  for (const note of notes) {
    if (typeof note === 'string') toast(note, { icon: '⚠️', duration: 8000 });
  }
}
